import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Fab,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { ProjectPlanResponse, ProjectTask } from "../types";
import {
  getProjectPlan,
  getGroupTasks,
  getProjectById,
  updateProjectTask,
} from "../services/projectService";
import {
  getProjectStatusColor,
  normalizeProjectColumnColor,
  taskStatusColors,
  TaskStatusKey,
} from "../utils/statusColors";
import { getCachedStatuses } from "../storage/preloadData";
import StatusChangeModal from "./Modals/StatusChangeModal";
import ProjectGroupForm from "./Forms/ProjectGroupForm";
import ConfirmDialog from "./Common/ConfirmDialog";
import ItemCard, { ItemWithSource } from "./Cards/ItemCard";
import { Project } from "../types";
import { useNetworkStatus } from "../hooks/useNetworkStatus";
import { isOfflineActionAvailable, getOfflineMessage } from "../utils/offlineGuard";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`group-tabpanel-${index}`}
      aria-labelledby={`group-tab-${index}`}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

interface ProjectTasksListProps {
  projectId: string;
}

const ProjectTasksList: React.FC<ProjectTasksListProps> = ({ projectId }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plan, setPlan] = useState<ProjectPlanResponse | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [groupTasks, setGroupTasks] = useState<Record<string, ProjectTask[]>>(
    {},
  );
  const [loadingGroups, setLoadingGroups] = useState<Set<string>>(new Set());
  const [groupFormOpen, setGroupFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [groupMenuAnchor, setGroupMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<ProjectTask | null>(null);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { isOnline } = useNetworkStatus();

  const projectColumns: Array<[string, string, string]> = Array.isArray(
    project?.columns,
  )
    ? project?.columns ?? []
    : [];
  const columnOptions = projectColumns.map(
    (column: [string, string, string]) => ({
      id: column?.[0],
      name: column?.[1],
      color: normalizeProjectColumnColor(column?.[2], column?.[1]),
    }),
  );
  const columnNames = columnOptions
    .map((column) => column.name)
    .filter((name): name is string => Boolean(name));
  const columnColors = columnOptions.reduce((acc, column) => {
    if (column.name) {
      acc[column.name] = column.color || "#757575";
    }
    return acc;
  }, {} as Record<string, string>);

  useEffect(() => {
    if (projectId) {
      loadProject();
      loadPlan();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (columnNames.length > 0) {
      return;
    }

    const loadStatuses = async () => {
      try {
        const statuses = await getCachedStatuses();
        const taskStatuses = statuses.tasks || [];
        if (taskStatuses.length > 0) {
          setAvailableStatuses(taskStatuses);
        } else {
          setAvailableStatuses([
            "Not Started",
            "Started",
            "Completed",
            "Deferred",
            "Сanceled",
          ]);
        }
      } catch (err) {
        console.error("Failed to load task statuses:", err);
        setAvailableStatuses([
          "Not Started",
          "Started",
          "Completed",
          "Deferred",
          "Сanceled",
        ]);
      }
    };
    loadStatuses();
  }, [columnNames]);

  const resolvedAvailableStatuses =
    columnNames.length > 0 ? columnNames : availableStatuses;

  const statusColorsMap: Record<string, string> =
    columnNames.length > 0
      ? columnColors
      : Object.keys(taskStatusColors).reduce(
          (acc, key) => {
            acc[key] = taskStatusColors[key as TaskStatusKey].color;
            return acc;
          },
          {} as Record<string, string>,
        );

  const handleStatusSave = async (newStatus: string, resolution?: string) => {
    if (!selectedTask) return;
    setIsLoading(true);
    try {
      const selectedColumn = columnOptions.find(
        (column) => column.name === newStatus,
      );
      const updateData: any = {
        columnName: newStatus,
        ...(selectedColumn?.id && { columnId: selectedColumn.id }),
      };
      if (newStatus === "Completed") {
        updateData.cSolution = resolution || "";
      }
      await updateProjectTask(selectedTask.id, updateData);
      setStatusModalOpen(false);
      setSelectedTask(null);
      await loadPlan();
    } catch (err: any) {
      console.error("Failed to update task status:", err);
      setError(err.message || "Ошибка при обновлении статуса задачи");
    } finally {
      setIsLoading(false);
    }
  };

  const loadProject = async () => {
    if (!projectId) return;
    try {
      const projectData = await getProjectById(projectId);
      setProject(projectData);
    } catch (err: any) {
      console.error("Failed to load project:", err);
    }
  };

  const loadPlan = async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const planData = await getProjectPlan(projectId);
      setPlan(planData);

      if (planData.groups.length > 0) {
        const firstGroup = planData.groups[0];
        if (
          firstGroup.tasks.list === null &&
          firstGroup.tasks.total !== null &&
          firstGroup.tasks.total > 0
        ) {
          loadGroupTasks(firstGroup.group.id);
        } else if (firstGroup.tasks.list) {
          setGroupTasks((prev) => ({
            ...prev,
            [firstGroup.group.id]: firstGroup.tasks.list || [],
          }));
        }
      }
    } catch (err: any) {
      console.error("Failed to load project plan:", err);
      setError(
        t("projects.detail.loadError") || "Не удалось загрузить план проекта",
      );
    } finally {
      setLoading(false);
    }
  };

  const loadGroupTasks = async (groupId: string) => {
    if (loadingGroups.has(groupId) || groupTasks[groupId]) {
      return;
    }

    setLoadingGroups((prev) => new Set(prev).add(groupId));
    try {
      const tasksData = await getGroupTasks(groupId);
      setGroupTasks((prev) => ({
        ...prev,
        [groupId]: tasksData.list || [],
      }));
    } catch (err: any) {
      console.error(`Failed to load tasks for group ${groupId}:`, err);
    } finally {
      setLoadingGroups((prev) => {
        const newSet = new Set(prev);
        newSet.delete(groupId);
        return newSet;
      });
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (plan && plan.groups[newValue]) {
      const group = plan.groups[newValue].group;
      const groupWithTasks = plan.groups[newValue];

      if (!groupTasks[group.id]) {
        if (
          groupWithTasks.tasks.list === null &&
          groupWithTasks.tasks.total !== null &&
          groupWithTasks.tasks.total > 0
        ) {
          loadGroupTasks(group.id);
        } else if (groupWithTasks.tasks.list) {
          setGroupTasks((prev) => ({
            ...prev,
            [group.id]: groupWithTasks.tasks.list || [],
          }));
        }
      }
    }
  };

  const handleGroupCreated = async () => {
    if (projectId) {
      const { removeFromCache, getCacheKeys } =
        await import("../storage/cacheManager");
      const allKeys = await getCacheKeys();
      const planCacheKeys = allKeys.filter(
        (key) =>
          key.includes(`/Project/${projectId}/plan`) ||
          key.includes(`Project/${projectId}/plan`),
      );
      await Promise.all(planCacheKeys.map((key) => removeFromCache(key)));
    }
    await loadPlan();
  };

  const handleGroupUpdated = async () => {
    await handleGroupCreated();
  };

  const handleRefresh = async () => {
    await loadPlan();
    if (plan && plan.groups[activeTab]) {
      const group = plan.groups[activeTab].group;
      setGroupTasks((prev) => {
        const newTasks = { ...prev };
        delete newTasks[group.id];
        return newTasks;
      });
      await loadGroupTasks(group.id);
    }
  };

  const handleGroupMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    groupId: string,
  ) => {
    event.stopPropagation();
    setGroupMenuAnchor(event.currentTarget);
    setSelectedGroupId(groupId);
  };

  const handleGroupMenuClose = () => {
    setGroupMenuAnchor(null);
    setSelectedGroupId(null);
  };

  const handleEditGroup = (groupId: string) => {
    const group = plan?.groups.find((g) => g.group.id === groupId);
    if (group) {
      setEditingGroup({ id: group.group.id, name: group.group.name });
      setGroupFormOpen(true);
    }
    handleGroupMenuClose();
  };

  const handleDeleteGroupClick = (groupId: string) => {
    const group = plan?.groups.find((g) => g.group.id === groupId);
    if (group) {
      setGroupToDelete({ id: group.group.id, name: group.group.name });
      setDeleteConfirmOpen(true);
    }
    handleGroupMenuClose();
  };

  const handleDeleteGroupConfirm = async () => {
    if (!groupToDelete || !projectId) return;
    setIsLoading(true);
    try {
      const { deleteProjectGroup } = await import("../services/projectService");
      await deleteProjectGroup(groupToDelete.id, projectId);
      setDeleteConfirmOpen(false);
      setGroupToDelete(null);
      await handleGroupCreated();
    } catch (err: any) {
      console.error("Failed to delete group:", err);
      setError(
        err?.response?.data?.message ||
          t("projects.detail.deleteError") ||
          "Не удалось удалить группу",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentGroupId = (): string | undefined => {
    if (plan && plan.groups[activeTab]) {
      return plan.groups[activeTab].group.id;
    }
    return undefined;
  };

  const getStatusConfig = (
    status: string,
    source: "task" | "meeting" = "task",
  ) => {
    const column = columnOptions.find((option) => option.name === status);
    if (column) {
      const normalized = status.toLowerCase().replace(/\s/g, "_");
      return {
        color: column.color || "#ccc",
        translation: t(`statuses.projectTask.${normalized}`, column.name),
      };
    }

    const normalized = status.toLowerCase().replace(/\s/g, "_");
    if (source === "task") {
      const key = status as TaskStatusKey;
      const text = t(`statuses.task.${normalized}`) || status;
      return {
        color: taskStatusColors[key]?.color || "#ccc",
        translation: text,
      };
    } else {
      const text = t(`statuses.meeting.${normalized}`) || status;
      return {
        color: getProjectStatusColor(status),
        translation: text,
      };
    }
  };

  if (loading && !plan) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          py: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 2 }}>
        {error}
      </Typography>
    );
  }

  if (plan && plan.groups.length === 0) {
    return (
      <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
        {t("projects.detail.noTasks") || "Нет задач"}
      </Typography>
    );
  }

  if (!plan) {
    return null;
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            flex: 1,
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
            },
          }}
        >
          {plan.groups.map((groupWithTasks, index) => {
            const group = groupWithTasks.group;
            return (
              <Tab
                key={group.id}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: activeTab === index ? 600 : 500,
                      }}
                    >
                      {group.name}
                    </Typography>
                    {groupWithTasks.tasks.total !== null && (
                      <Chip
                        label={groupWithTasks.tasks.total}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.75rem",
                          backgroundColor: "rgba(0, 0, 0, 0.08)",
                          fontWeight: 600,
                        }}
                      />
                    )}
                    <IconButton
                      size="small"
                      component="span"
                      onClick={(e) => handleGroupMenuOpen(e, group.id)}
                      sx={{
                        ml: 0.5,
                        p: 0.5,
                        "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
              />
            );
          })}
        </Tabs>
        <IconButton
          onClick={() => setGroupFormOpen(true)}
          sx={{
            ml: 2,
            border: "1px solid #4467A5",
            borderRadius: "8px",
            bgcolor: "#4467A5",
            color: "#fff",
            "&:hover": { bgcolor: "#385894" },
          }}
          size="small"
        >
          <AddIcon />
        </IconButton>
      </Box>

      {plan.groups.map((groupWithTasks, index) => {
        const group = groupWithTasks.group;
        const tasks = groupTasks[group.id] || groupWithTasks.tasks.list || [];
        const isLoading = loadingGroups.has(group.id);

        return (
          <TabPanel key={group.id} value={activeTab} index={index}>
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 4,
                }}
              >
                <CircularProgress />
              </Box>
            ) : tasks.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                {t("projects.detail.noTasks") || "Нет задач"}
              </Typography>
            ) : (
              <>
                <Box sx={{ display: { xs: "none", sm: "block" } }}>
                  <Box
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                      p: { xs: 1, sm: 2 },
                      bgcolor: "background.paper",
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {[
                        t("tasks.columnNames.name"),
                        t("tasks.columnNames.assignee"),
                        t("tasks.columnNames.startDate"),
                        t("tasks.columnNames.endDate"),
                        t("tasks.columnNames.status"),
                      ].map((label, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            flex: idx === 0 ? "0 0 20%" : "0 0 20%",
                            textAlign: idx === 0 ? "left" : "center",
                          }}
                        >
                          <Typography variant="body1">{label}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {tasks.map((task) => {
                    const item: ItemWithSource = {
                      id: task.id,
                      name: task.name,
                      assignedUserName: task.assignedUserName || undefined,
                      status: (task.columnName ||
                        task.status ||
                        "Not Started") as TaskStatusKey,
                      dateStart: task.dateStart || "",
                      dateEnd: task.dateEnd || "",
                      source: "task",
                    };
                    return (
                      <ItemCard
                        key={task.id}
                        item={item}
                        getStatusConfig={getStatusConfig}
                        customNavigate={(item) =>
                          `/project/${projectId}/task/${item.id}`
                        }
                        onStatusChange={handleRefresh}
                        onStatusUpdate={async (
                          newStatus: string,
                          resolution?: string,
                        ) => {
                          const selectedColumn = columnOptions.find(
                            (column) => column.name === newStatus,
                          );
                          const updateData: any = {
                            columnName: newStatus,
                            ...(selectedColumn?.id && {
                              columnId: selectedColumn.id,
                            }),
                          };
                          if (newStatus === "Completed") {
                            updateData.cSolution = resolution || "";
                          }
                          await updateProjectTask(task.id, updateData);
                          await handleRefresh();
                        }}
                        onStatusUpdateSuccess={(message) => {}}
                        statusTypeOverride="projectTask"
                        availableStatusesOverride={columnNames}
                        statusColorsOverride={columnColors}
                        currentStatusOverride={
                          task.columnName || task.status || ""
                        }
                      />
                    );
                  })}
                </Box>
              </>
            )}
          </TabPanel>
        );
      })}

      <Fab
        color="primary"
        size="small"
        aria-label="add task"
        onClick={async () => {
          const { available, reason } = await isOfflineActionAvailable(
            "create",
            t,
          );
          if (!available) {
            setError(reason || getOfflineMessage("create", t));
            return;
          }
          const currentGroupId = getCurrentGroupId();
          const url = currentGroupId
            ? `/project/${projectId}/task/new?groupId=${currentGroupId}`
            : `/project/${projectId}/task/new`;
          navigate(url);
        }}
        disabled={!isOnline}
        sx={{
          display: { xs: "flex", sm: "none" },
          position: "fixed",
          bottom: 62,
          right: 16,
          border: "1px solid #4467A5",
          borderRadius: "8px",
          bgcolor: "#4467A5",
          color: "#fff",
          "&:hover": { bgcolor: "#385894" },
          boxShadow: "0px 2px 3.6px rgba(0, 0, 0, 0.36)",
        }}
      >
        <AddIcon />
      </Fab>

      {project && (
        <ProjectGroupForm
          open={groupFormOpen}
          projectId={projectId}
          projectName={project.name}
          group={
            editingGroup
              ? plan?.groups.find((g) => g.group.id === editingGroup.id)
                  ?.group || null
              : null
          }
          onClose={() => {
            setGroupFormOpen(false);
            setEditingGroup(null);
          }}
          onCreated={handleGroupCreated}
          onUpdated={handleGroupUpdated}
        />
      )}
      <Menu
        anchorEl={groupMenuAnchor}
        open={Boolean(groupMenuAnchor)}
        onClose={handleGroupMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={() => selectedGroupId && handleEditGroup(selectedGroupId)}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("common.edit") || "Редактировать"} />
        </MenuItem>
        <MenuItem
          onClick={() =>
            selectedGroupId && handleDeleteGroupClick(selectedGroupId)
          }
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: "#D32F2F" }} />
          </ListItemIcon>
          <ListItemText primary={t("common.delete") || "Удалить"} />
        </MenuItem>
      </Menu>
      <ConfirmDialog
        open={deleteConfirmOpen}
        title={
          t("projects.detail.deleteGroupConfirmTitle") ||
          "Подтверждение удаления"
        }
        content={
          groupToDelete
            ? t("projects.detail.deleteGroupConfirmContent", {
                name: groupToDelete.name,
              }) ||
              `Вы уверены, что хотите удалить группу "${groupToDelete.name}"?`
            : ""
        }
        onConfirm={handleDeleteGroupConfirm}
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setGroupToDelete(null);
        }}
        loading={isLoading}
      />
      {selectedTask && (
        <StatusChangeModal
          open={statusModalOpen}
          onClose={() => {
            setStatusModalOpen(false);
            setSelectedTask(null);
          }}
          onSave={handleStatusSave}
          currentStatus={selectedTask.columnName || selectedTask.status || ""}
          availableStatuses={resolvedAvailableStatuses}
          statusType="projectTask"
          statusColors={statusColorsMap}
          isLoading={isLoading}
          currentResolution={selectedTask.cSolution || selectedTask.resolution}
        />
      )}
    </Box>
  );
};

export default ProjectTasksList;
